import type { RefObject } from "react";
import { useEffect, useRef } from "react";

import { useDirection } from "@jti/fe-common";

const MIN_DISTANCE = 60;

type UseSwipeBackParams = {
  ref: RefObject<HTMLElement | null>;
  enabled: boolean;
  onSwipeBack: () => void;
};

export function useSwipeBack({ ref, enabled, onSwipeBack }: UseSwipeBackParams) {
  const isRtl = useDirection() === "rtl";
  const stateRef = useRef<{ startX: number; startY: number; isTracking: boolean }>({
    startX: 0,
    startY: 0,
    isTracking: false,
  });

  useEffect(() => {
    const node = ref.current;
    if (!enabled || !node) return;

    const handleTouchStart = (event: TouchEvent) => {
      const touch = event.touches[0];
      if (!touch) return;
      stateRef.current = { startX: touch.clientX, startY: touch.clientY, isTracking: true };
    };

    const handleTouchEnd = (event: TouchEvent) => {
      const state = stateRef.current;
      if (!state.isTracking) return;
      state.isTracking = false;

      const touch = event.changedTouches[0];
      if (!touch) return;

      const dx = touch.clientX - state.startX;
      const dy = touch.clientY - state.startY;

      // Ignore mostly-vertical gestures so scrolling a long submenu never goes back.
      if (Math.abs(dx) <= Math.abs(dy)) return;
      if (Math.abs(dx) < MIN_DISTANCE) return;

      // Back is swipe-right (dx > 0) in LTR, swipe-left (dx < 0) in RTL.
      const isBackDirection = isRtl ? dx < 0 : dx > 0;
      if (isBackDirection) {
        onSwipeBack();
      }
    };

    node.addEventListener("touchstart", handleTouchStart, { passive: true });
    node.addEventListener("touchend", handleTouchEnd, { passive: true });

    return () => {
      node.removeEventListener("touchstart", handleTouchStart);
      node.removeEventListener("touchend", handleTouchEnd);
    };
  }, [ref, enabled, isRtl, onSwipeBack]);
}
